import { FunctionComponent, SVGProps } from 'react'
import { generatePath } from 'react-router-dom'
import { AtlasObjectType, IAtlasObjectRef } from '@common/helpers/atlasUrls'
import { IAtlasAISuggestion } from '@common/interfaces/atlasai'
import { ClientInfoStatus } from '@common/interfaces/client'
import { MentionType } from '@common/interfaces/mentions'
import { PhotoSubjectType } from '@common/interfaces/photo'
import { ROUTES } from '../../../constants/routes'
import { getReportingDocumentRoute } from '../../../helpers/reportingRoutes'

const images = import.meta.glob('../../../assets/images/**/*', {
  eager: true,
  query: '?url',
  import: 'default',
})

// Mention icons rendered inline as SVG components so their `currentColor`
// fill/stroke inherits the surrounding (theme-aware) text color. Loading them
// via `?url` + <img> forces them black, which is invisible in dark mode.
const mentionIconComponents = import.meta.glob<FunctionComponent<SVGProps<SVGSVGElement>>>(
  '../../../assets/images/note-mention-*-icon.svg',
  { eager: true, query: '?react', import: 'default' },
)

// Build suggestion data for Atlas AI mentions
export const buildAtlasAISuggestionData = ({
  id,
  type,
  clientStatus,
  reportType,
}: Partial<IAtlasAISuggestion>): {
  link: string | null
  avatar: string
  className: string | null
} => {
  switch (type) {
    case MentionType.InternalUser:
    case MentionType.ClientUser:
      // No user detail page exists, so the chip renders without a link.
      return {
        link: null,
        avatar: 'user',
        className: null,
      }

    case MentionType.Client:
      if (clientStatus === ClientInfoStatus.DueDiligence) {
        return {
          link: generatePath(ROUTES.DUE_DILIGENCE, { id }),
          avatar: 'client-underwriting',
          className: null,
        }
      }
      if ([ClientInfoStatus.Prospect, ClientInfoStatus.Archived].includes(clientStatus)) {
        return {
          link: generatePath(ROUTES.PROSPECT_PAGE, { id }),
          avatar: 'client-prospect',
          className: null,
        }
      }
      return {
        link: generatePath(ROUTES.CLIENT_PAGE, { id }),
        avatar: 'client',
        className: null,
      }

    case MentionType.Entity:
      return {
        link: generatePath(ROUTES.ENTITY_PAGE, { id }),
        avatar: 'entity',
        className: null,
      }

    case MentionType.BBC:
      return {
        link: generatePath(ROUTES.BBC_SUMMARY, { id }),
        avatar: 'bbc',
        className: null,
      }

    case MentionType.OngoingReporting:
      return {
        link: getReportingDocumentRoute(reportType || '', id || ''),
        avatar: 'ongoing-reporting',
        className: null,
      }

    case MentionType.DueDiligence:
      return {
        link: generatePath(ROUTES.DUE_DILIGENCE, { id }),
        avatar: 'client-underwriting',
        className: null,
      }

    case MentionType.Prospect:
      return {
        link: generatePath(ROUTES.PROSPECT_PAGE, { id }),
        avatar: 'client-prospect',
        className: null,
      }

    case MentionType.Contact:
      return {
        link: generatePath(ROUTES.CRM_PERSON_PAGE, { id }),
        avatar: 'contact',
        className: null,
      }

    // A team names a group of people, so there is no record page to open — the
    // two-person glyph is what tells it apart from the person chip beside it.
    case MentionType.Team:
      return {
        link: null,
        avatar: 'team',
        className: null,
      }

    case MentionType.Company:
      return {
        link: generatePath(ROUTES.CRM_COMPANY_PAGE, { id }),
        avatar: 'company',
        className: null,
      }

    case MentionType.Organization:
      return {
        link: `${generatePath(ROUTES.ORGANIZATION_PAGE, { id })}?source=organization`,
        avatar: 'organization',
        className: null,
      }

    case MentionType.Opportunity:
      return {
        link: null, // No specific route for opportunity detail page yet
        avatar: 'opportunity',
        className: null,
      }

    // `@here`/`@channel` name a group of people rather than a record, so there is
    // nothing to link to — the bullhorn is what tells them apart at a glance from
    // the person chip sitting next to them.
    case MentionType.Here:
    case MentionType.Channel:
      return {
        link: null,
        avatar: 'broadcast',
        className: null,
      }

    default:
      return {
        link: null,
        avatar: null,
        className: null,
      }
  }
}

const MENTION_ATLAS_TYPES: Partial<Record<MentionType, AtlasObjectType>> = {
  [MentionType.InternalUser]: AtlasObjectType.User,
  [MentionType.Entity]: AtlasObjectType.Entity,
  [MentionType.BBC]: AtlasObjectType.BBC,
  [MentionType.OngoingReporting]: AtlasObjectType.OngoingReporting,
  [MentionType.DueDiligence]: AtlasObjectType.DueDiligence,
  [MentionType.Prospect]: AtlasObjectType.Prospect,
  [MentionType.Contact]: AtlasObjectType.Person,
  [MentionType.Company]: AtlasObjectType.Company,
  [MentionType.Organization]: AtlasObjectType.Organization,
}

/**
 * The record a mention names, for its preview card. Null for a group of people — a team, a
 * broadcast — for a client user, or for a record kind with no preview. A client's id is always a
 * client_info row, whatever page its link opens at its stage.
 */
export const mentionAtlasRef = ({
  id,
  type,
  clientStatus,
}: {
  id?: string
  type?: MentionType
  clientStatus?: string
}): IAtlasObjectRef | null => {
  if (!id) return null
  // Mentions saved before `type` existed were always teammates — the server's
  // extractMentions reads a missing type the same way.
  if (!type) return { type: AtlasObjectType.User, id }
  if (type === MentionType.Client) {
    const isDueDiligence = (clientStatus as ClientInfoStatus) === ClientInfoStatus.DueDiligence
    return { type: isDueDiligence ? AtlasObjectType.DueDiligence : AtlasObjectType.Client, id }
  }
  const atlasType = MENTION_ATLAS_TYPES[type]
  return atlasType ? { type: atlasType, id } : null
}

// Human-readable section labels for grouping the suggestion dropdown by type
// (used by task-comment tagging). Keyed by MentionType.
export const MENTION_TYPE_LABELS: Partial<Record<MentionType, string>> = {
  [MentionType.InternalUser]: 'People',
  [MentionType.Team]: 'Teams',
  [MentionType.ClientUser]: 'Client Users',
  [MentionType.Client]: 'Clients',
  [MentionType.DueDiligence]: 'Due Diligence',
  [MentionType.Entity]: 'Entities',
  [MentionType.BBC]: 'Borrowing Bases',
  [MentionType.OngoingReporting]: 'Reporting',
  [MentionType.Prospect]: 'Prospects',
  [MentionType.Contact]: 'Contacts',
  [MentionType.Company]: 'Companies',
  [MentionType.Organization]: 'Organizations',
  [MentionType.Opportunity]: 'Opportunities',
  [MentionType.AmendmentAnalysis]: 'Amendments',
}

/**
 * The photo subject behind a mention — the face or logo to draw for it.
 *
 * Null for a kind that names something rather than someone: a borrowing base, a
 * report, a broadcast token. Those keep their type icon, which is the only thing
 * that tells them apart, whereas a person or a company has a picture of its own
 * and reads better as that picture than as a generic glyph.
 */
export const mentionPhotoSubject = (type?: MentionType): PhotoSubjectType | null => {
  switch (type) {
    case MentionType.InternalUser:
    case MentionType.ClientUser:
      return PhotoSubjectType.Users
    // Both are client_info rows; only their lifecycle stage differs. Prospect is
    // deliberately absent: its mention id is an ops_reporting row (see the
    // Prospect branch of resolveMentionSuggestions), not a client_info one, so a
    // photo lookup with it could only ever miss.
    case MentionType.Client:
    case MentionType.DueDiligence:
      return PhotoSubjectType.Clients
    case MentionType.Entity:
      return PhotoSubjectType.Entities
    case MentionType.Company:
      return PhotoSubjectType.Company
    case MentionType.Organization:
      return PhotoSubjectType.Organizations
    case MentionType.Contact:
      return PhotoSubjectType.People
    default:
      return null
  }
}

const salesforceAvatars = ['contact', 'company', 'organization', 'opportunity']

export const isSalesforceAvatar = (avatar: string): boolean => {
  return salesforceAvatars.includes(avatar)
}

export const getSalesforceCloudUrl = (): string | null => {
  const imagePath = Object.keys(images).find((path) => path.includes('salesforce-blue-cloud.png'))
  if (imagePath && images[imagePath]) {
    const imageUrl = images[imagePath]
    // Handle both string URLs and imported module objects
    if (typeof imageUrl === 'string') {
      return imageUrl
    }
    // If it's an object with a default property (common with some bundlers)
    if (imageUrl && typeof imageUrl === 'object' && 'default' in imageUrl) {
      return (imageUrl as { default: string }).default
    }
  }
  return null
}

export const getMentionAvatarLink = (avatar: string): string | null => {
  const imagePath = Object.keys(images).find((path) =>
    path.includes(`note-mention-${avatar}-icon.svg`),
  )
  if (imagePath && images[imagePath]) {
    const imageUrl = images[imagePath]
    // Handle both string URLs and imported module objects
    if (typeof imageUrl === 'string') {
      return imageUrl
    }
    // If it's an object with a default property (common with some bundlers)
    if (imageUrl && typeof imageUrl === 'object' && 'default' in imageUrl) {
      return (imageUrl as { default: string }).default
    }
  }
  return null
}

export const getMentionAvatarComponent = (
  avatar: string,
): FunctionComponent<SVGProps<SVGSVGElement>> | null => {
  const iconPath = Object.keys(mentionIconComponents).find((path) =>
    path.includes(`note-mention-${avatar}-icon.svg`),
  )
  return iconPath ? mentionIconComponents[iconPath] : null
}
